import { finalizeSuccessfulAuth } from './passwordAuth';
import {
  completeDeviceSrpAuth,
  confirmAndRememberDevice,
  getCachedDeviceKey,
  hydrateUserDeviceContext,
  clearDeviceContextFromStorage,
  isCognitoDeviceDoesNotExistError,
} from './deviceTracking';
import {
  clearActiveChallengeState,
  ensureUserHasChallengeSession,
  getChallengeUserForUsername,
  setActiveChallengeState,
} from './challengeState';

const parseJson = (value, fallback) => {
  try {
    if (!value) return fallback;
    return JSON.parse(value);
  } catch (_) {
    return fallback;
  }
};

const toNewPasswordRequiredPayload = ({ email, session, challengeParameters }) => {
  const userAttributes = parseJson(challengeParameters?.userAttributes, null);
  const rawRequiredAttributes = parseJson(challengeParameters?.requiredAttributes, []);
  const requiredAttributes = Array.isArray(rawRequiredAttributes)
    ? rawRequiredAttributes.map((attr) => String(attr).replace(/^userAttributes\./, ''))
    : [];

  return {
    email,
    session,
    userAttributes,
    requiredAttributes,
  };
};

const respondToAuthChallenge = ({ userPool, user, challengeName, challengeResponses }) => {
  const clientId = userPool.getClientId();
  const deviceKey = getCachedDeviceKey(user);
  const buildResponses = (includeDevice) => ({
    ...(challengeResponses || {}),
    ...(includeDevice && deviceKey ? { DEVICE_KEY: deviceKey } : {}),
  });

  const makeRequest = (includeDevice) => ({
    ChallengeName: challengeName,
    ChallengeResponses: buildResponses(includeDevice),
    ClientId: clientId,
    Session: user.Session,
  });

  return new Promise((resolve, reject) => {
    try {
      const attempt = (includeDevice) => {
        user.client.request('RespondToAuthChallenge', makeRequest(includeDevice), (err, data) => {
          if (!err) {
            resolve(data);
            return;
          }

          if (includeDevice && deviceKey && isCognitoDeviceDoesNotExistError(err)) {
            // Stale device keys -> clear and retry without DEVICE_KEY.
            clearDeviceContextFromStorage({
              clientId,
              usernames: [user?.username, challengeResponses?.USERNAME],
            });
            attempt(false);
            return;
          }

          reject(err);
        });
      };

      attempt(true);
    } catch (e) {
      reject(e);
    }
  });
};

const respondToEmailOtpChallenge = async ({ userPool, user, username, code }) => {
  const attempt = async (challengeResponses) => {
    return respondToAuthChallenge({
      userPool,
      user,
      challengeName: 'EMAIL_OTP',
      challengeResponses,
    });
  };

  try {
    return await attempt({ USERNAME: username, EMAIL_OTP_CODE: code });
  } catch (err) {
    const message = String(err?.message || err || '');

    if (/ANSWER/i.test(message)) {
      return attempt({ USERNAME: username, ANSWER: code });
    }

    if (/EMAIL_OTP_CODE/i.test(message)) {
      return attempt({ USERNAME: username, EMAIL_OTP_CODE: code });
    }

    return attempt({ USERNAME: username, EMAIL_OTP_CODE: code, ANSWER: code });
  }
};

export const respondToMfaChallenge = async ({ userPool, username, code, session, challengeName }) => {
  const user = getChallengeUserForUsername(username, userPool);

  const hasSession = ensureUserHasChallengeSession({ user, username, sessionFromCall: session });
  if (!hasSession) {
    return { success: false, error: 'Missing challenge session. Please sign in again.' };
  }

  if (challengeName === 'CUSTOM_CHALLENGE') {
    return new Promise((resolve) => {
      user.sendCustomChallengeAnswer(code, {
        onSuccess: (result, userConfirmationNecessary) => {
          if (userConfirmationNecessary === true && typeof user?.setDeviceStatusRemembered === 'function') {
            try {
              user.setDeviceStatusRemembered({ onSuccess: () => {}, onFailure: () => {} });
            } catch (_) {
              // ignore
            }
          }
          clearActiveChallengeState();
          resolve({ success: true, user: result });
        },
        onFailure: (err) => {
          resolve({ success: false, error: err });
        },
      });
    });
  }

  if (challengeName === 'EMAIL_OTP') {
    try {
      const data = await respondToEmailOtpChallenge({ userPool, user, username, code });

      if (data?.AuthenticationResult) {
        try {
          const authenticationResult = data.AuthenticationResult;
          finalizeSuccessfulAuth(user, authenticationResult);
          await confirmAndRememberDevice({ user, authenticationResult });
          clearActiveChallengeState();
          return { success: true, user: user.signInUserSession };
        } catch (e) {
          return { success: false, error: e };
        }
      }

      if (data?.ChallengeName === 'DEVICE_SRP_AUTH') {
        // Complete remembered-device verification automatically.
        try {
          user.Session = data?.Session;
          const clientId = userPool.getClientId();
          hydrateUserDeviceContext({ user, clientId, usernames: [username] });
          const cognitoUserSession = await completeDeviceSrpAuth({ user, session: user.Session });
          clearActiveChallengeState();
          return { success: true, user: cognitoUserSession };
        } catch (e) {
          return { success: false, error: e };
        }
      }

      if (data?.ChallengeName) {
        // Follow-up challenge.
        user.Session = data?.Session;
        setActiveChallengeState({
          user,
          username,
          challengeName: data.ChallengeName,
          session: user.Session,
        });
        return {
          success: false,
          challenge: data.ChallengeName,
          payload: data.ChallengeName === 'NEW_PASSWORD_REQUIRED'
            ? toNewPasswordRequiredPayload({
              email: username,
              session: user.Session,
              challengeParameters: data?.ChallengeParameters || {},
            })
            : {
              email: username,
              session: user.Session,
              challengeParameters: data?.ChallengeParameters || {},
            },
        };
      }

      return { success: false, error: 'Unknown EMAIL_OTP response' };
    } catch (e) {
      return { success: false, error: e };
    }
  }

  // SMS_MFA / SOFTWARE_TOKEN_MFA
  return new Promise((resolve) => {
    user.sendMFACode(code, {
      onSuccess: (result, userConfirmationNecessary) => {
        if (result?.challengeName === 'NEW_PASSWORD_REQUIRED' || result?.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
          const challengeParameters = result?.challengeParam || result?.ChallengeParameters || {};
          user.Session = result?.Session || user.Session;
          setActiveChallengeState({
            user,
            username,
            challengeName: 'NEW_PASSWORD_REQUIRED',
            session: user.Session,
          });
          resolve({
            success: false,
            challenge: 'NEW_PASSWORD_REQUIRED',
            payload: toNewPasswordRequiredPayload({
              email: username,
              session: user.Session,
              challengeParameters,
            }),
          });
          return;
        }

        if (userConfirmationNecessary === true && typeof user?.setDeviceStatusRemembered === 'function') {
          try {
            user.setDeviceStatusRemembered({ onSuccess: () => {}, onFailure: () => {} });
          } catch (_) {
            // ignore
          }
        }
        clearActiveChallengeState();
        resolve({ success: true, user: result });
      },
      onFailure: (err) => {
        resolve({ success: false, error: err });
      },
    }, challengeName);
  });
};
